 
import { IconInfoCircleFilled } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useEffect, useRef, useState } from 'react';
import { NSTable } from 'bricks';
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import { useCustomScroll } from 'views/ProFormaTable/hooks/useCustomScroll';
import { Tooltip } from 'reactstrap';
import { format, parseISO } from 'date-fns';
import PeriodCellValue from 'views/ProFormaTable/components/ProForma/ProFormaDevelopment/DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';
import { ProFormaModelIds } from 'constants/proForma';
import {
    PRO_FORMA_RETURN_TOOLTIPS, AggregateReportProperties, PropertyKeyToLabelMap, spacerRows,
    CostMetricKeyToLabelMap,
    Periods,
    RETURNS_STICKY_COLUMNS_WIDTH_PX,
    LandDevPropertyKeyToLabelMap,
    landDevSpacerRows,
} from './constant';

export interface IPermanentDebtRow {
    key: string;
    label: string;
}

export interface IProps {
    propertyReturns: any;
    dateKeys: string[];
    operationBeginMonth: number;
    showCents: boolean;
    cadence: string;
    proFormaModelId?: number;
    fillMissingPeriodsWithZero?: boolean;
}

export const ProFormaReturnsTable = ({
    propertyReturns,
    dateKeys,
    operationBeginMonth,
    showCents,
    cadence,
    proFormaModelId,
    fillMissingPeriodsWithZero = false,
}: IProps) => {
    const {
        proFormaReturns, proFormaReturnsCashFlow, cashFlowData, costMetrics, permanentDebtRowsByLineItem,
    }: { permanentDebtRowsByLineItem?: Record<string, IPermanentDebtRow[]>; [key: string]: any } = propertyReturns;

    const isLandDevelopment = proFormaModelId === ProFormaModelIds.LAND_DEVELOPMENT;
    const activePropertyKeyToLabelMap = isLandDevelopment ? LandDevPropertyKeyToLabelMap : PropertyKeyToLabelMap;
    const activeSpacerRows = isLandDevelopment ? landDevSpacerRows : spacerRows;

    const propertyRows: IPermanentDebtRow[] = Object.entries(activePropertyKeyToLabelMap).flatMap(([key, propLabel]) => {
        const perLoanRows = isLandDevelopment ? undefined : permanentDebtRowsByLineItem?.[key];
        if (perLoanRows?.length) {
            return perLoanRows;
        }
        return key in proFormaReturns ? [{ key, label: propLabel as string }] : [];
    });
    const spacerAfterKeys = new Set<string>();
    if (!isLandDevelopment && permanentDebtRowsByLineItem) {
        [permanentDebtRowsByLineItem.totalPermanentLoanFunding, permanentDebtRowsByLineItem.totalPermanentLoanDebtService]
            .forEach(rows => {
                if (rows?.length) spacerAfterKeys.add(rows[rows.length - 1].key);
            });
    }
    const [openTooltips, setOpenTooltips] = useState<Record<string, boolean>>({});
    const {
        tableRef,
        scrollbarRef,
        scrollbarContentRef,
        handleTableScroll,
    } = useCustomScroll();
    const operationStartRef = useRef<HTMLTableCellElement>(null);

    useEffect(() => {
        if (operationStartRef.current && operationBeginMonth && tableRef.current) {
            const headerCell = operationStartRef.current;
            tableRef.current.scrollLeft = headerCell.offsetLeft - RETURNS_STICKY_COLUMNS_WIDTH_PX;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationStartRef.current]);

    const toggleTooltip = (tooltipId: string) => {
        setOpenTooltips(prev => ({ ...prev, [tooltipId]: !prev[tooltipId] }));
    };

    const formatDate = (date: string, cadence?: string) => {
        try {
            if (cadence === Periods.ANNUALLY) {
                return format(parseISO(date), 'yyyy');
            }
            return format(parseISO(date), 'MMM yyyy').toUpperCase();
        } catch {
            return date;
        }
    };

    const formatValue = (value: number | null) => {
        if (value === null) return '-';
        if (value < 0) {
            return `(${numeralFormatterCurrency(Math.abs(value), showCents ? '$0,0.00' : '$0,0')})`;
        }
        return numeralFormatterCurrency(value, showCents ? '$0,0.00' : '$0,0');
    };

    const renderHeader = (date: string, index: number | string, disabled: boolean = false, cadence: string = Periods.MONTHLY) => {
        const isOperationMonth = index === operationBeginMonth;
        const monthClassName = disabled ?
            'h6 NSTable__thead__tr__th--grey-text NSTable__thead__tr__th--opacity-3 text-uppercase text-nowrap text-secondary text-right mb-0'
            : 'h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-secondary text-right mb-0';
        const dateClassName = disabled ?
            'h6 text-uppercase text-right text-nowrap NSTable__thead__tr__th--opacity-3 mt-1'
            : 'h6 mt-1 text-uppercase text-right text-nowrap text-white';
        const isMonthly = cadence === Periods.MONTHLY;
        return (
            <th
                key={`header-${date}`}
                className="NSTable__thead__tr__th text-nowrap py-1"
                ref={isOperationMonth ? operationStartRef : undefined}
                style={{ width: '140px', minWidth: '140px' }}
            >
                <div className={monthClassName}>
                    {isMonthly ? 'MONTH' : 'YEAR' }
                    {' '}
                    {index}
                </div>
                <div className={dateClassName}>
                    {formatDate(date, cadence)}
                </div>
            </th>
        );
    };

    const formatReturnValues = (value: number | null, metricName: string) => {
        if (value === null) return '-';
        if (metricName === 'developmentSpread') {
            return `${numeralFormatterCurrency(value, '0,0')} bps`;
        }
        return percentFormatter((value || 0) * 100);
    };

    return (
        <div>
            <div
                className="NSTable--sticky-headers__table-container"
                ref={tableRef}
                onScroll={() => handleTableScroll('table')}
                style={{ maxHeight: 'none' }}
            >
                <NSTable className="NSTable NSTable--sticky-headers NSTable--sticky-headers--two-sticky-columns mb-1 w-100">
                    <thead className="NSTable__thead">
                        <tr key="NSTable__thead__tr header-row">
                            <th className="align-middle text-white NSTable__thead__tr__th p-2" key="property-header">Summary</th>
                            <th className="align-middle text-white text-right p-2" key="amount-header">Amount</th>
                            {dateKeys.length > 0 ?
                                dateKeys.map((date, index) => renderHeader(date, index, false, cadence))
                                : renderHeader('MON YEAR', '#', true)}
                        </tr>
                    </thead>
                    <tbody>
                        {propertyRows.map(({ key, label: propLabel }) => {
                            const data = proFormaReturns[key];
                            const tooltip = PRO_FORMA_RETURN_TOOLTIPS[propLabel as AggregateReportProperties];
                            const isPerLoanRow = key.startsWith('permanentLoan_');
                            const isSpacerRow = spacerAfterKeys.has(key) ||
                                (!isPerLoanRow && activeSpacerRows.includes(propLabel as AggregateReportProperties));

                            return (
                                <React.Fragment key={`${key}-fragment`}>
                                    <tr key={`${key}-row`} className="NSTable__tbody__tr">
                                        <td className="text-white">
                                            {propLabel}
                                            {tooltip && (
                                                <>
                                                    <NSIcon
                                                        className="px-1 text-white"
                                                        id={`${key}-tooltip`}
                                                        icon={IconInfoCircleFilled}
                                                    />
                                                    <Tooltip
                                                        target={`${key}-tooltip`}
                                                        isOpen={openTooltips[`${key}-tooltip`] || false}
                                                        toggle={() => toggleTooltip(`${key}-tooltip`)}
                                                        placement="top"
                                                        delay={{ show: 0, hide: 300 }}
                                                        style={{
                                                            textAlign: 'left',
                                                            minWidth: '250px',
                                                        }}
                                                    >
                                                        <span className="font-weight-bold">{propLabel}</span>
                                                        <div>{tooltip}</div>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </td>
                                        <td className="text-white text-right">
                                            {formatValue(data as number)}
                                        </td>
                                        {dateKeys.length > 0 ? dateKeys.map(date => (
                                            <td
                                                key={`${key}-period-${date}`}
                                                className="text-white text-right"
                                                style={{ width: '140px', minWidth: '140px' }}
                                            >
                                                {formatValue(proFormaReturnsCashFlow[key]?.[date] ?? (fillMissingPeriodsWithZero ? 0 : null))}
                                            </td>
                                        )) : (
                                            <PeriodCellValue
                                                period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                                value="—"
                                                cellClassName="text-white text-right"
                                            />
                                        )}
                                    </tr>
                                    {isSpacerRow && (
                                        <tr className="NSTable__spacer-row" key={`${key}-spacer-row`}>
                                            <td colSpan={dateKeys.length + 2} className="pt-0 pb-1" />
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </NSTable>

                <div className="pb-2" />

                <NSTable className="NSTable NSTable--sticky-headers--four-sticky-columns">
                    <thead>
                        <tr>
                            <th className="align-middle text-white NSTable__thead__tr__th p-2" key="property-label-header">Returns</th>
                            <th className="align-middle text-white text-right p-2" key="equity-multiple-header">Equity multiple</th>
                            <th className="align-middle text-white text-right p-2" key="equity-irr">IRR</th>
                            <th className="align-middle text-white text-right p-2" key="equity-net-profit">Net profit</th>
                            {dateKeys.length > 0 ?
                                dateKeys.map((date, index) => renderHeader(date, index, false, cadence)) : renderHeader('MON YEAR', '#', true)}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(cashFlowData).map(([key, data]: [string, any]) => {
                            const propLabel = PropertyKeyToLabelMap[key as keyof typeof PropertyKeyToLabelMap];
                            return (
                                <tr key={`cash-flow-${key}`} className="NSTable__tbody__tr">
                                    <td className="text-white" key={`cash-flow-label-${key}`}>
                                        {propLabel}
                                    </td>
                                    <td className="text-white text-right" key={`cash-flow-equity-multiple-${key}`}>
                                        {data.equityMultiple != null ? `${numeralFormatterCurrency(data.equityMultiple, '0,0.00')}x` : '-'}
                                    </td>
                                    <td className="text-white text-right" key={`cash-flow-irr-${key}`}>
                                        {data.irr !== null ? percentFormatter((data.irr || 0) * 100) : '-'}
                                    </td>
                                    <td className="text-white text-right" key={`cash-flow-net-profit-${key}`}>
                                        {formatValue(data.netProfit)}
                                    </td>
                                    {dateKeys.length > 0 ? dateKeys.map(date => (
                                        <td key={`cash-flow-${key}-period-${date}`} className="text-white text-right">
                                            {formatValue(data.periods[date] ?? (fillMissingPeriodsWithZero ? 0 : null))}
                                        </td>
                                    )) : (
                                        <PeriodCellValue
                                            period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                            value="—"
                                            cellClassName="text-white text-right"
                                        />
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </NSTable>
            </div>
            <div className="NSTable--custom-scrollbar__wrapper mb-2">
                <div
                    className="NSTable--custom-scrollbar"
                    ref={scrollbarRef}
                    onScroll={() => handleTableScroll('scrollbar')}
                >
                    <div className="NSTable--custom-scrollbar__content" ref={scrollbarContentRef} />
                </div>
            </div>
            {!isLandDevelopment && (
                <NSTable className="NSTable text-dark">
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr text-right">
                            <th className="NSTable__thead__tr__th align-middle" key="label-header" />
                            <th className="NSTable__thead__tr__th align-middle" key="untrended-header">Untrended</th>
                            <th className="NSTable__thead__tr__th align-middle" key="trended-header">Trended</th>
                            <th className="NSTable__thead__tr__th align-middle" key="sale-header">Sale</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(costMetrics).map(([metricName, metric]: [string, any]) => (
                            <tr key={`cost-metric-${metricName}`} className="NSTable__tbody__tr">
                                <td
                                    className="align-middle"
                                    key={`${metricName}-label`}
                                >
                                    {CostMetricKeyToLabelMap[metricName as keyof typeof CostMetricKeyToLabelMap]}
                                </td>
                                <td className="align-middle text-right" key={`${metricName}-untrended`}>
                                    {formatReturnValues(metric.untrended, metricName)}
                                </td>
                                <td className="align-middle text-right" key={`${metricName}-trended`}>
                                    {formatReturnValues(metric.trended, metricName)}
                                </td>
                                <td className="align-middle text-right" key={`${metricName}-sale`}>
                                    {formatReturnValues(metric.sale, metricName)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </NSTable>
            )}
        </div>
    );
};

export default ProFormaReturnsTable;
